import { FC } from 'react';
import { Input, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { get, isEmpty } from 'lodash';
import { Form } from '~/components';
import WizardForm from '~/components/WizardForm';
import { Draft } from '~/types/Draft';
import PersonFormItems from '~/pages/ksa/application/managers/components/PersonFormItems';
import { useGetApplication, useGetPayloadWithProvided, useSubmitStep } from '~/controllers/wizard';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import { Stage } from '~/types/Application';
import { KsaCompanyInformation } from '~/types/ksa/KsaCompanyInformation';
import { KsaPerson } from '~/types/ksa/KsaPerson';
import { useCommonErrors } from '~/controllers/validation';
import { getIndividualsFormName } from './individuals/IndividualEdit';
import ShareholdersProofs from './ShareholdersProofs';

const ShareholdersSoleProprietor: FC = () => {
  const { t } = useTranslation('ksaApplication');
  const [form] = Form.useForm<Draft<'shareholders'>>();

  const applicationQuery = useGetApplication();
  const payload = useGetPayloadWithProvided();
  const managers = payload?.provided?.managers?.managers;
  const isCheckedAuthorizedSignatory = isEmpty(managers);
  const isSelectedCompanyLicense =
    isCheckedAuthorizedSignatory &&
    (payload.companyInformation as KsaCompanyInformation)?.thiqahLegalType === '101';
  const nameArr = getIndividualsFormName(0);

  const individualFromProvided: KsaPerson | undefined = get(
    payload?.provided,
    getIndividualsFormName(0)
  );
  const individualFromApplication: KsaPerson | undefined = get(
    applicationQuery?.data?.payload,
    getIndividualsFormName(0)
  );

  const [, handleError] = useCommonErrors();

  const submitMutation = useSubmitStep();
  return (
    <WizardForm<Draft<'shareholders'>>
      payloadKey="shareholders"
      form={form}
      stage={Stage.ksaApplication}
      onFinish={(result) => {
        submitMutation.mutateAsync(result).catch((error) => {
          const code = error?.response?.data?.code;
          if (
            code === 'MOBILE_VERIFICATION_INVALID_NUMBER' ||
            code === 'MOBILE_VERIFICATION_INVALID_ID'
          ) {
            handleError.onError([{ name: ['MOBILE'], errors: [] }]);
          }
          if (code === 'MOBILE_VERIFICATION_NOT_OWNER') {
            handleError.onError([{ name: ['MOBILE_NOT_OWNER'], errors: [] }]);
          }
        });
      }}
    >
      <ShareholdersProofs />
      <PersonFormItems
        form={form}
        name={nameArr}
        isCheckedAuthorizedSignatory={isCheckedAuthorizedSignatory}
        isSelectedCompanyLicense={isSelectedCompanyLicense}
        hasPassport={!!individualFromProvided?.passport?.documentNumber}
        personalDocumentType={individualFromProvided?.personalDocument?.idType}
        hasVerifiedPhoneNumber={
          !!individualFromProvided?.personalData?.phoneNumber ||
          !!individualFromApplication?.personalData?.phoneNumber
        }
        isSignedWithEmail={!!individualFromProvided?.personalData?.email}
      />
      <section>
        <Typography.Title level={5}>{t('Ownership share:').toUpperCase()}</Typography.Title>
        <InputsGroupLayout>
          <Form.Item
            label={t('Shares, %')}
            name={[...nameArr, 'ownershipShare', 'percent']}
            disabled
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t('Shares count')}
            name={[...nameArr, 'ownershipShare', 'count']}
            disabled
          >
            <Input />
          </Form.Item>
        </InputsGroupLayout>
      </section>
    </WizardForm>
  );
};

export default ShareholdersSoleProprietor;
